import { ComponentProps } from 'react';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import { Preconnect } from './Preconnect';
import { PreloadFont } from './PreloadFont';

const DEFAULT_VIEWPORT =
  'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover';

interface NextAppHeadProps {
  /**
   * viewport meta tag 설정
   * @default 'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover'
   */
  viewport?: string;
  /**
   * next-seo 설정
   * SEO 메타 태그 설정에 활용됩니다
   */
  seo?: ComponentProps<typeof NextSeo>;
}

/**
 * Next.js 앱 전용 Head 컴포넌트입니다.
 *
 * **주의: 이 컴포넌트는 Next.js 앱에서만 사용 가능합니다.**
 * React SPA (예: zigzin)에서는 사용할 수 없습니다.
 *
 * 다음 기능을 제공합니다:
 * - viewport meta tag
 * - Preconnect (DNS prefetch, preconnect)
 * - PreloadFont (폰트 프리로드)
 * - NextSeo (next-seo 기반 SEO 메타 태그)
 *
 * @example
 * #### 기본 사용 (_app.tsx)
 * ```tsx
 * import { NextAppHead, GlobalStyles } from '@common/components';
 * import { RootContextProvider } from '@common/contexts';
 *
 * const MyApp = ({ Component, pageProps }) => {
 *   return (
 *     <RootContextProvider>
 *       <NextAppHead seo={pageProps.seo} />
 *       <GlobalStyles />
 *       <Component {...pageProps} />
 *     </RootContextProvider>
 *   );
 * };
 * ```
 *
 * @example
 * #### 페이지별 SEO 설정 (getServerSideProps / getStaticProps)
 * ```tsx
 * // pages/products/[id].tsx
 * import { GetServerSideProps } from 'next';
 *
 * export const getServerSideProps: GetServerSideProps = async ({ params }) => {
 *   const product = await fetchProduct(params.id);
 *
 *   return {
 *     props: {
 *       product,
 *       seo: {
 *         title: product.name,
 *         description: product.description,
 *         openGraph: {
 *           title: product.name,
 *           description: product.description,
 *           images: [{ url: product.imageUrl }],
 *         },
 *       },
 *     },
 *   };
 * };
 * ```
 *
 * @example
 * #### 커스텀 viewport 설정
 * ```tsx
 * // user-scalable=yes 를 허용하는 페이지
 * <NextAppHead viewport="width=device-width,initial-scale=1.0,user-scalable=yes" />
 * ```
 */
export const NextAppHead = ({ viewport = DEFAULT_VIEWPORT, seo }: NextAppHeadProps) => {
  return (
    <>
      <Head>
        <meta name='viewport' content={viewport} />
      </Head>
      <Preconnect />
      <PreloadFont />
      {seo && <NextSeo {...seo} />}
    </>
  );
};
